import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from 'react-dropzone';

import {
  Box,
  Flex,
  Form,
  Text,
  toast,
  Button,
  Devider,
  FormContext,
  FormErrorTypes,
  IOnUploadOptionsProps,
  WorkingWeekHoursInput,
  useServerResponseTranslation,
} from 'dash-ui';
import { useTheme } from 'styled-components';
import { DashValidation, FileModel, WeekDays } from 'dash-common';

import useFormDraftFileUpload from 'common/hooks/useDraftFileUpload';
import { usePermissionCheck } from 'common/hooks/usePermissionCheck/usePermissionCheck';
import {
  useGetCurrentDeveloperUserQuery,
  useEditInternalSaleTeamByOrganizationIdMutation,
  GetCurrentDeveloperUserDocument,
} from 'gql/generated.graphql';
import { SaveSaleFormFields } from './InternalSaleTemForm.constants';
import { useSaveExternalTeamFormValidation } from './InternalSaleTemForm.hooks';
import { FlexWraper, FormGroupWraper } from 'pages/OrganizationPage/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/InternalSaleTeamForm/InternalSaleTeamForm.style';

function InternalSaleTeamForm() {
  const theme = useTheme();
  const { t: tCommonLabels } = useTranslation('common', {
    keyPrefix: 'labels',
  });

  const { t } = useTranslation(['forms', 'pages']);
  const formRef = useRef<HTMLFormElement | null>(null);
  const { validate, isError } = useSaveExternalTeamFormValidation();

  const translateServerError = useServerResponseTranslation();
  const {
    stateHandlers,
    errorHandlers,
    formState,
    formErrors,
    formOnSubmit,
    setFormValues,
    resetFormErrors,
  } = useContext(FormContext);
  const [isLogoLoading, setIsLogoLoading] = useState(false);
  const draftFileUpload = useFormDraftFileUpload();
  const { data, loading } = useGetCurrentDeveloperUserQuery();
  const isAllowedToEdit = usePermissionCheck();

  const { saleTeam, organizationId } = useMemo(() => {
    const { _id: organizationId, internalSaleTeam } =
      data?.getCurrentDeveloperUser?.organizations?.[0] || {};

    return { saleTeam: internalSaleTeam, organizationId };
  }, [data]);

  const [editInternalSaleTeamByOrganizationId, { loading: editLoading }] =
    useEditInternalSaleTeamByOrganizationIdMutation({
      refetchQueries: [GetCurrentDeveloperUserDocument],
    });

  const initialFormValues = useMemo(() => {
    if (!saleTeam) return {};

    const { name, website, logoFile, phone, workingHours } = saleTeam;

    return {
      name,
      url: website,
      workingHours,
      logo: logoFile?._id,
      phone: { number: phone?.number, dialCode: phone?.dialCode },
    };
  }, [saleTeam]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setFormValues(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues]);

  const onCancel = () => {
    resetFormErrors();
    setFormValues(initialFormValues);
  };
  const handleFieldsValues =
    (field: SaveSaleFormFields) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      stateHandlers[field](value);
      validate(field, value);
    };

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();

    const { name, url, logo, workingHours, phone } = formState;
    const variables = {
      organizationId,
      input: {
        name,
        website: url,
        logoFileId: logo,
        ...(phone?.dialCode && phone?.number && { phone }),
        ...(workingHours && {
          workingHours: Object.values(WeekDays).reduce(
            (memo: any, key: string) => {
              const { to, from } = workingHours[key] || {};

              return { ...memo, ...(to && from && { [key]: { to, from } }) };
            },
            {}
          ),
        }),
      },
    };

    editInternalSaleTeamByOrganizationId({
      variables,
      onCompleted: () => {
        toast.success(t(`pages:externalSalesPage.toasts.teamChanged`));
      },
      onError: (error: any) => {
        toast.error(translateServerError(error));
      },
    });
  };

  const onFileChanged = (fileId: string) => {
    stateHandlers[SaveSaleFormFields.Logo](fileId || null);
    errorHandlers[SaveSaleFormFields.Logo](null);
    setIsLogoLoading(false);
  };

  const onFileUpload = (file: File, options: IOnUploadOptionsProps) => {
    errorHandlers[SaveSaleFormFields.Logo](null);
    setIsLogoLoading(true);
    draftFileUpload(file, options);
  };

  const isLoading = isLogoLoading || editLoading || loading;

  return (
    <Form onSubmit={submitForm} ref={formRef} px={6} pt={2} pb={6}>
      <FlexWraper>
        <Box flex={1}>
          <FormGroupWraper>
            <Form.Label>{t`forms:labels.teamLogo`}</Form.Label>
            <Form.FileUpload.Single
              disabled={!isAllowedToEdit}
              minHeight="146px"
              accept={{ 'image/png': ['.png'], 'image/jpg': ['.jpg'] }}
              onUpload={onFileUpload}
              onChanged={onFileChanged}
              initialFile={saleTeam?.logoFile as FileModel}
              maxSize={DashValidation.MaxLogoFileSize}
              onDropRejected={(error) => {
                let message = 'forms:messages:uploadErrors:unknown';
                if (error.code === ErrorCode.FileInvalidType) {
                  message = 'forms:messages:uploadErrors:fileInvalidType';
                }
                if (error.code === ErrorCode.FileTooLarge) {
                  message = 'forms:messages:uploadErrors:fileTooLarge';
                }
                errorHandlers[SaveSaleFormFields.Logo]({
                  type: FormErrorTypes.Invalid,
                  message: t(message, {
                    type: '.png, .jpg',
                    maxSize: DashValidation.MaxLogoFileSize / 1024 / 1024,
                  }),
                });
              }}
              $errorType={formErrors[SaveSaleFormFields.Logo]?.type}
            />
            <Form.Feedback
              $errorType={formErrors[SaveSaleFormFields.Logo]?.type}
            >
              {formErrors[SaveSaleFormFields.Logo]?.message}
            </Form.Feedback>
          </FormGroupWraper>
        </Box>
        <Box flex={2}>
          <Form.Group mt={1}>
            <Form.Label required>{t`labels.name`}</Form.Label>
            <Form.Input
              w="100%"
              disabled={!isAllowedToEdit}
              placeholder={t`forms:placeholders.saleTeamName` || ''}
              onChange={handleFieldsValues(SaveSaleFormFields.Name)}
              value={formState.name || ''}
              $errorType={formErrors[SaveSaleFormFields.Name]?.type}
            />
            <Form.Feedback
              $errorType={formErrors[SaveSaleFormFields.Name]?.type}
            >
              {formErrors[SaveSaleFormFields.Name]?.message}
            </Form.Feedback>
          </Form.Group>
          <Form.Group mt={1}>
            <Form.Label>{t`forms:labels.url`}</Form.Label>
            <Form.Input
              w="100%"
              disabled={!isAllowedToEdit}
              placeholder={t`forms:placeholders.url` || ''}
              onChange={handleFieldsValues(SaveSaleFormFields.Url)}
              value={formState.url || ''}
              $errorType={formErrors[SaveSaleFormFields.Url]?.type}
            />
            <Form.Feedback
              $errorType={formErrors[SaveSaleFormFields.Url]?.type}
            >
              {formErrors[SaveSaleFormFields.Url]?.message}
            </Form.Feedback>
          </Form.Group>
          <Form.Group w="100%" mt={1}>
            <Form.Label>{t('forms:labels.phoneNumber')}</Form.Label>
            <Form.InputPhone
              disabled={!isAllowedToEdit}
              dialCode={formState.phone?.dialCode || ''}
              defaultValue={formState.phone?.number || ''}
              placeholder={t('forms:placeholders.phoneNumber') || ''}
              onChangePhoneValue={({ dialCode, number }) =>
                stateHandlers[SaveSaleFormFields.Phone]({
                  dialCode,
                  number,
                })
              }
            />
          </Form.Group>
        </Box>
      </FlexWraper>
      <Devider size={1} my={4} color={theme.colors.n30} />
      <Box mb={4}>
        <Text bold>{t('forms:labels.salesWorkingHours')}</Text>
      </Box>
      <WorkingWeekHoursInput
        formField={SaveSaleFormFields.WorkingHours}
        disabled={!isAllowedToEdit}
      />
      <Form.Group mt={-2}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {formState.errorSlug}
        </Form.Feedback>
      </Form.Group>
      {isAllowedToEdit && (
        <Flex>
          <Button
            type="button"
            px={9}
            mr={2}
            onClick={() => {
              if (formRef.current) {
                formRef.current.requestSubmit();
              }
            }}
            disabled={isLoading || isError}
            isLoading={isLoading}
          >
            {tCommonLabels('save')}
          </Button>
          <Button type="button" px={7} variation="outline" onClick={onCancel}>
            {tCommonLabels('cancel')}
          </Button>
        </Flex>
      )}
    </Form>
  );
}

export default InternalSaleTeamForm;
